import React, { useEffect, useState } from "react";
import { Box, Container } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useFetchTable } from "@hooks/useFetchDataTable";
import BackgroundImage from "@components/BackgroundImageCst";
import Table from "@components/Table";
import HeroCst from "@components/HeroCst";
import Breadcrumb from "@components/Breadcrumb";
import { useParams } from "react-router-dom";
import { useFetchDataDetail } from "@hooks/useFetchDetail";
import { ServicesURL } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import { fetcher } from "src/services/config";
import { MethodType } from "src/services/type";
import useSWRMutation from "swr/mutation";
import { Group } from "src/interfaces/Group";
import ButtonBackPage from "@components/ButtonBackPage";
import AnimationFadeIn from "@components/AnimationFadeIn";
import { useAuth } from "@context/auth";
import { useFilters } from "@context/filters";
import {
  checkAssociationChanged,
  GetLeaderboardClusterData as GetClusterData,
  LinkLeaderboardClusterBody as LinkClusterBody,
} from "../types";

const AssociationGroup: React.FC = () => {
  const { id, initiativeId, clusterId } = useParams();
  const { user } = useAuth();
  const { filters } = useFilters();
  const filterId = `${user?.id_user}_${PagesID["corporate.initiative.leaderboard-cluster.association.group"]}`;

  const { t } = useTranslation(["leaderboard-cluster-page"]);

  const [groupAssociation, setGroupAssociation] = useState<
    Record<number, boolean>
  >({});

  const { columns, dataTable } = useFetchTable<Group[]>(
    `${ServicesURL.groups.replace(":idCorporate", `${id ? id : 1}`)}/groups`,
    PagesID["corporate.groups-dynamic"],
    filters[filterId],
  );

  const { dataDetail, mutate } = useFetchDataDetail(
    ServicesURL.getLeaderboardCluster
      .replace(":corporateId", id!)
      .replace(":initiativeId", initiativeId!)
      .replace(":clusterId", clusterId!),
    PagesID["corporate.initiative.leaderboard-cluster.association.group"],
  );

  useEffect(() => {
    if (dataDetail) {
      setGroupAssociation(
        ((dataDetail as GetClusterData).groups ?? []).reduce<
          Record<number, boolean>
        >((agg, cur) => {
          agg[cur.id] = true;
          return agg;
        }, {}),
      );
    }
  }, [dataDetail]);

  const { trigger: triggerGroupAssociation } = useSWRMutation(
    ServicesURL.linkLeaderboardCluster
      .replace(":corporateId", id!)
      .replace(":initiativeId", initiativeId!)
      .replace(":clusterId", clusterId!),
    (url: string, { arg }: { arg: LinkClusterBody }) => {
      return fetcher<any>(`${url}`, MethodType.PUT, arg);
    },
  );

  const handleRowSelection = async (groups: Record<number, boolean>) => {
    if (checkAssociationChanged(groupAssociation, groups)) {
      const groupIds = Object.entries(groups)
        .filter(([_, v]) => !!v)
        .map(([k]) => Number(k));
      const body: LinkClusterBody = {
        groups: groupIds,
      };
      await triggerGroupAssociation(body);
      mutate && mutate();
      setGroupAssociation(groups);
    }
  };

  const name = !Array.isArray(dataDetail as GetClusterData | [])
    ? (dataDetail as GetClusterData | undefined)?.cluster.name
    : "";

  return (
    <AnimationFadeIn>
      <BackgroundImage
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-corp-initiatives.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst
          title={`${name} - ${t("association-groups.hero-title")}`}
          description={t("association-groups.hero-description")}
        />
        <ButtonBackPage />
        <Box
          sx={{
            mt: "25px",
          }}
        >
          <Table
            id={
              PagesID[
                "corporate.initiative.leaderboard-cluster.association.group"
              ]
            }
            data={dataTable?.output}
            columns={columns || []}
            configurationTopToolbar={[]}
            enableRowSelection={true}
            enableRowActions={false}
            enableMultiRowSelection={true}
            selectedRows={groupAssociation}
            onRowSelectionChange={(groups: any) => {
              handleRowSelection(groups);
            }}
            getRowId={(originalRow: any) => originalRow?.id}
            totalPages={dataTable?.metadata?.total_elements}
            modAssociationEnabled={true}
          />
        </Box>
      </Container>
    </AnimationFadeIn>
  );
};

export default AssociationGroup;
